'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import type { AnalyticsConfig } from '../types';
import { CosmicAnalytics } from '../lib/analytics';

let analyticsInstance: CosmicAnalytics | null = null;

/**
 * Initialize the analytics instance
 */
export function initAnalytics(config?: AnalyticsConfig): CosmicAnalytics {
  if (!analyticsInstance) {
    analyticsInstance = new CosmicAnalytics(config);
  }
  return analyticsInstance;
}

/**
 * Get the current analytics instance
 */
export function getAnalytics(): CosmicAnalytics | null {
  return analyticsInstance;
}

/**
 * React hook for Cosmic Analytics with Next.js App Router
 */
export function useCosmicAnalytics(config?: AnalyticsConfig) {
  const pathname = usePathname();
  const initializedRef = useRef(false);
  const prevUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // Initialize analytics on first render
    if (!initializedRef.current) {
      initAnalytics(config);
      initializedRef.current = true;
    }

    const analytics = getAnalytics();
    if (!analytics) return;

    // Track page view when URL changes
    // We use window.location.href to get the full URL including search params
    // This avoids needing useSearchParams which requires Suspense
    const currentUrl = typeof window !== 'undefined' ? window.location.href : pathname;
    
    if (prevUrlRef.current !== currentUrl) {
      analytics.trackPageView();
      prevUrlRef.current = currentUrl;
    }

    // Cleanup function to track page exit
    return () => {
      analytics.trackPageExit();
    };
  }, [pathname, config]);
} 